import React, {ReactNode, useEffect, useMemo, useRef} from "react";
import {ELEMENT_ID, IPropsElement, isHasLabel} from "./constants";
import styles from "./style.module.scss";
import {CalendarOutlined, ClockCircleOutlined, SwapRightOutlined,} from "@ant-design/icons";
import {callEventChecklistEdit, getChecklistEdit, useChecklistEdit} from "./ChecklistEditContext";
import Icon, {ICON_NAME} from "../../../components/Icon";
import Menu from "../../../components/Menu";
import { useSortable } from "@dnd-kit/react/sortable";
import { pointerIntersection } from "@dnd-kit/collision";
import { isVerticalDividePreview } from "./dragDrop";

interface IProps {
    data: IPropsElement;
}

const CheckBox: React.FC<IProps> = ({data}) => {
    const {
        options = "",
        numberOfColumns = "",
        optionDefault = "",
    } = data.properties;
    const value = Array.isArray(optionDefault)
        ? new Set(optionDefault)
        : new Set([]);
    const view =
        options.length > 0
            ? options
                .split("\n")
                .reduce((list: ReactNode[], item: string, index: number) => {
                    if (item.trim().length > 0)
                        list = [
                            ...list,
                            <div
                                className={styles.check}
                                key={`${item} ${index}`}
                                data-type-check="checkbox"
                                data-is-check={value.has(index.toString())}
                            >
                                {item}
                            </div>,
                        ];
                    return list;
                }, [])
            : [];
    const style: any = {"--number-of-columns": numberOfColumns};
    return <div className={styles.control} style={style}>
        <div className={styles.columns}>{view}</div>
    </div>;
};

const DatePicker: React.FC<IProps> = ({data}) => {
    const placeHolderTime = "HH : MM";
    const {subLabel, separator, isShowTime, isCalendarPopup} = data.properties;
    const placeHolderDate = ["YYYY", "MM", "DD"].join(` ${separator} `);
    return (
        <div className={styles.control}>
            <div className={styles.placeholder}>
				<span>
					{isShowTime
                        ? [placeHolderDate, placeHolderTime].join(", ")
                        : placeHolderDate}
				</span>
                {isCalendarPopup && (
                    <div className="absolute right-[11px] top-[0]">
                        <CalendarOutlined/>
                    </div>
                )}
            </div>
            <div className={styles.subLabel}>{subLabel || "Sub label"}</div>
        </div>
    );
};

const Divide: React.FC<IProps> = () => {
    return <div className={styles.divide}/>;
};

const Dropdown: React.FC<IProps> = ({data}) => {
    const {subLabel = "", optionDefault, options} = data.properties;
    const opts = options?.split("\n") || [];
    const view = Array.isArray(optionDefault)
        ? optionDefault.map((index) => opts[+index]).join(", ")
        : optionDefault
            ? opts[+optionDefault]
            : "";
    return (
        <div className={styles.control}>
            <div className={styles.placeholder}>
                {view === JSON.stringify("") ? "" : view}
                <div className="absolute right-[12px] top-[1px]">
                    <Icon icon={ICON_NAME.ARROW_DOWN} size="small"/>
                </div>
            </div>
            <div className={styles.subLabel}>{subLabel || "Sub label"}</div>
        </div>
    );
};

const Header: React.FC<IProps> = ({data}) => {
    const {
        label,
        properties: {subLabel, textAlign, fontSize},
    } = data;
    const style = {textAlign};
    return (
        <div className={styles.headerText} style={style}>
            <label data-font-size={fontSize} data-is-empty={label.length === 0}>
                {label || "Heading text"}
            </label>
            <p className={styles.subLabel}>{subLabel || "Sub label"}</p>
        </div>
    );
};

const LongText: React.FC<IProps> = ({data}) => {
    const {placeHolder = "", subLabel = ""} = data.properties;
    return (
        <div className={styles.control}>
            <div
                className={styles.placeholder}
                data-is-empty={placeHolder.length === 0}
            >
                {placeHolder || "Placeholder"}
            </div>
            <div className={styles.subLabel}>{subLabel || "Sub label"}</div>
        </div>
    );
};

const Number: React.FC<IProps> = ({data}) => {
    const {placeHolder = "", subLabel = ""} = data.properties;
    return (
        <div className={styles.control}>
            <div
                className={styles.placeholder}
                data-is-empty={placeHolder.length === 0}
            >
                {placeHolder || "Placeholder"}
            </div>
            <div className={styles.subLabel}>{subLabel || "Sub label"}</div>
        </div>
    );
};

const Radio: React.FC<IProps> = ({data}) => {
    const {
        options = "",
        numberOfColumns = "",
        optionDefault = "",
    } = data.properties;
    const value = Array.isArray(optionDefault) ? "" : optionDefault;
    const view =
        options.length > 0
            ? options.split("\n").reduce((list: any[], item: string, index) => {
                if (item.trim().length > 0)
                    list = [
                        ...list,
                        <div
                            key={item + index}
                            className={styles.check}
                            data-type-check="radio"
                            data-is-check={index.toString() === value.toString()}
                        >
                            {item}
                        </div>,
                    ];
                return list;
            }, [])
            : [];
    const style: any = {"--number-of-columns": numberOfColumns};

    return (
        <div className={styles.control} style={style}>
            <div className={styles.columns}>{view}</div>
        </div>
    );
};

const ShortText: React.FC<IProps> = ({data}) => {
    const {placeHolder = "", subLabel = ""} = data.properties;
    return (
        <div className={styles.control}>
            <div
                className={styles.placeholder}
                data-is-empty={placeHolder.length === 0}
            >
                {placeHolder || "Placeholder"}
            </div>
            <div className={styles.subLabel}>{subLabel || "Sub label"}</div>
        </div>
    );
};

const Sign: React.FC<IProps> = ({data}) => {
    const {options = ""} = data.properties;

    const view = useMemo(() => {
        try {
            const value = JSON.parse(options || "")
            return Array.isArray(value) ? value : [];
        } catch (e) {
            console.log("Json error: ", e);
            return []
        }
    }, [options]);

    return (
        <div className={styles.control}>
            <div className={styles.sign}>
                {view.map((item: any) => <div key={item.value}>{item.label}</div>)}
            </div>
        </div>
    );
};

const Time: React.FC<IProps> = ({data}) => {
    const placeHolderTime = "HH : MM";
    const {subLabel, isTimeRange} = data.properties;

    return (
        <div className={styles.control}>
            <div className={styles.placeholder}>
                <span>{placeHolderTime}</span>
                {isTimeRange && (
                    <>
                        <SwapRightOutlined className="ml-2 mr-2"/>
                        <span>{placeHolderTime}</span>
                    </>
                )}
                <div className="absolute right-[10px] top-[0]">
                    <ClockCircleOutlined/>
                </div>
            </div>
            <div className={styles.subLabel}>{subLabel || "Sub label"}</div>
        </div>
    );
};

const Views: { [id: string]: React.FC<IProps> } = {
    [ELEMENT_ID.CHECKBOX]: CheckBox,
    [ELEMENT_ID.DATE]: DatePicker,
    [ELEMENT_ID.DIVIDE]: Divide,
    [ELEMENT_ID.DROPDOWN]: Dropdown,
    [ELEMENT_ID.HEADER]: Header,
    [ELEMENT_ID.LONG_TEXT]: LongText,
    [ELEMENT_ID.NUMBER]: Number,
    [ELEMENT_ID.RADIO]: Radio,
    [ELEMENT_ID.SHORT_TEXT]: ShortText,
    [ELEMENT_ID.SIGN]: Sign,
    [ELEMENT_ID.TIME]: Time,
};

export const ElementPreview: React.FC<IProps> = ({ data }) => {
    const { label, type } = data;
    const View = Views[type];
    return <>
        {isHasLabel.has(type) && <label className="mb-[6px]" data-is-empty={label.length === 0}>
            {label || "Label"}
        </label>}
        {View && <View data={data} />}
    </>;
};

const MiddleElement: React.FC<{ rowIndex: number, position: number } & IProps> = ({ data, rowIndex, position }) => {
    const [selected, set] = useChecklistEdit(state => state.selected);
    const get = getChecklistEdit();
    const wasSelected = useRef(false);
    const elementRef = useRef<HTMLDivElement | null>(null);
    const [shouldFocus] = useChecklistEdit(state => state.focusElement === data.index);
    const { onElementDelete, onElementDuplicate } = callEventChecklistEdit();
    const [previewPosition] = useChecklistEdit(state => (
        state.palettePreview?.rowIndex === rowIndex ? state.palettePreview.position : undefined
    ));
    const [originalVerticalDivide] = useChecklistEdit(state => (
        data.type === ELEMENT_ID.DIVIDE && isVerticalDividePreview(state.form, rowIndex, false, data.index)
    ));
    const { ref, isDragging } = useSortable({
        id: `element-${data.index}`,
        collisionDetector: pointerIntersection,
        index: position + (previewPosition !== undefined && previewPosition <= position ? 1 : 0),
        group: `elements-${rowIndex}`,
        accept: 'checklist-element',
        collisionPriority: 1,
        transition: { duration: 250 },
        type: 'checklist-element',
        data: { rowIndex, position, element: data, verticalDivide: originalVerticalDivide },
    });
    useEffect(() => {
        if (!shouldFocus) return;
        const frame = requestAnimationFrame(() => {
            elementRef.current?.focus({ preventScroll: true });
            elementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            set({ focusElement: undefined });
        });
        return () => cancelAnimationFrame(frame);
    }, [shouldFocus, set]);

    return <Menu asChild trigger={['contextMenu']} items={[{
        key: 'duplicate',
        label: <div className='menu-line'>
            <Icon icon={ICON_NAME.DUPLICATE} size={15} />
            <span>Duplicate</span>
        </div>,
        onClick: () => {
            set({ selected: data.index, rowSelected: rowIndex });
            onElementDuplicate(data);
        },
    }, {
        key: 'delete',
        label: <div className='menu-line' data-danger={true}>
            <Icon icon={ICON_NAME.DELETE} size={15} danger={true} />
            <span>Delete</span>
        </div>,
        onClick: () => {
            set({ selected: data.index, rowSelected: rowIndex });
            onElementDelete(data.index);
        },
    }]}>
        <div
            ref={element => { elementRef.current = element; ref(element); }}
            tabIndex={0}
            role="group"
            aria-label={data.label || 'Checklist field'}
            className={`${styles.element} relative touch-none ${isDragging ? 'opacity-40' : ''}`}
            data-selected={data.index === selected}
            data-checklist-kind={data.type}
            data-checklist-index={data.index}
            data-checklist-position={position}
            onMouseDown={event => {
                event.stopPropagation();
                wasSelected.current = event.button === 0 && selected === data.index;
                set({ selected: data.index, rowSelected: rowIndex });
            }}
            onClick={event => {
                event.stopPropagation();
                if (event.defaultPrevented || !wasSelected.current || get().selected !== data.index) return;
                let target: Element | null = event.target as Element;
                while (target && target !== event.currentTarget && !target.matches('label')
                    && !target.classList.contains(styles.placeholder) && !target.classList.contains(styles.subLabel)) {
                    target = target.parentElement;
                }
                if (!target || target === event.currentTarget) return;
                const property = target.matches('label') ? 'label'
                    : target.classList.contains(styles.subLabel) ? 'subLabel' : 'placeHolder';
                const input = get().getParentEl()?.querySelector<HTMLInputElement>(
                    `input[data-checklist-property="${property}"]`
                );
                input?.focus({ preventScroll: true });
                input?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }}
            onContextMenu={event => {
                event.stopPropagation();
                set({ selected: data.index, rowSelected: rowIndex });
            }}
        >
            <ElementPreview data={data} />
        </div>
    </Menu>;
};

export default MiddleElement;
